import type { FormEvent, KeyboardEvent, ReactNode } from "react";
import { useId } from "react";
import styled, { keyframes } from "styled-components";

import { color } from "../../theme/color";
import { Button } from "./controls";

type DialogProps = {
    title: string;
    children: ReactNode;
    submit?: string;
    canSubmit?: boolean;
    cancel?: string;
    onSubmit?(): void;
    onClose(): void;
};

/**
 * A dialog that slides up from the bottom on a phone, and shows in the middle on a tablet. It has a Cancel button,
 * and a submit button labelled `submit`. Without `submit`, there's only a Close button.
 */
export function Dialog({ title, children, submit, canSubmit = true, cancel, onSubmit, onClose }: DialogProps) {
    const titleId = useId();
    const closeOnEscape = (event: KeyboardEvent) => event.key === "Escape" && onClose();

    function send(event: FormEvent) {
        event.preventDefault();

        if (canSubmit) onSubmit?.();
    }

    return (
        <Scrim onClick={(event) => event.target === event.currentTarget && onClose()}>
            <Panel role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={send} onKeyDown={closeOnEscape}>
                <Title id={titleId}>{title}</Title>
                {children}
                <Actions>
                    <Button type="button" onClick={onClose}>
                        {cancel ?? (submit ? "Cancel" : "Close")}
                    </Button>
                    {submit && (
                        <Button type="submit" $kind="primary" disabled={!canSubmit}>
                            {submit}
                        </Button>
                    )}
                </Actions>
            </Panel>
        </Scrim>
    );
}

const rise = keyframes`
    from { transform: translateY(24px); opacity: 0; }
`;

const Scrim = styled.div`
    position: fixed;
    inset: 0;
    z-index: 20;
    background: ${color.scrim};
    display: flex;
    align-items: flex-end;
    justify-content: center;

    @media (min-width: 700px) {
        align-items: center;
    }
`;

const Panel = styled.form`
    width: 100%;
    max-height: 90vh;
    overflow: auto;
    background: ${color.background};
    border-radius: 20px 20px 0 0;
    padding: 20px 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: ${rise} 0.2s ease-out;

    @media (min-width: 700px) {
        width: 480px;
        border-radius: 20px;
        padding: 24px;
    }
`;

const Title = styled.h2`
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 700;
`;

const Actions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 4px;

    & > * {
        flex: 1;
    }

    @media (min-width: 700px) {
        & > * {
            flex: none;
        }
    }
`;
